import { z } from 'zod';
import { adminAuditFieldsSchema } from './IAdminAudit';

/**
 * Reminder-Regel (reminders.ReminderRule). system_mail/expected_status kommen
 * vom Backend als PrimaryKeyRelatedField, also als reine IDs (kein Nested-Objekt).
 */
export const reminderRuleSchema = z
    .object({
        id: z.number().default(-1),
        name: z.string().default(''),
        trigger: z.string().default(''),
        offset_seconds: z.number().default(0),
        send_time_local: z.string().nullable().default(null),
        audience: z.string().default(''),
        guard: z.string().default('none'),
        expected_status: z.number().nullable().default(null),
        system_mail: z.number().default(-1),
        sender_account: z.string().default(''),
        channel: z.string().default('email'),
        mandatory: z.boolean().default(true),
        max_lateness_seconds: z.number().default(21600),
        is_active: z.boolean().default(false),
    })
    .merge(adminAuditFieldsSchema);

export type IReminderRule = z.infer<typeof reminderRuleSchema>;

/** Vom Backend beschreibbare Felder (create/update-Payload). */
export type IReminderRuleWritable = Pick<
    IReminderRule,
    | 'name'
    | 'trigger'
    | 'offset_seconds'
    | 'send_time_local'
    | 'audience'
    | 'guard'
    | 'expected_status'
    | 'system_mail'
    | 'sender_account'
    | 'channel'
    | 'mandatory'
    | 'max_lateness_seconds'
>;

/** Ein registrierter Anchor (Trigger) aus /reminder-rules/metadata/. */
export const anchorMetaSchema = z.object({
    key: z.string(),
    model_label: z.string(),
    anchor_type: z.enum(['date', 'datetime']),
    expected_status_mode: z.enum(['forbidden', 'optional', 'required']),
    allowed_audiences: z.array(z.string()),
    allowed_guards: z.array(z.string()),
    allowed_template_vars: z.array(z.string()),
    has_latest_send_bound: z.boolean(),
});

export type IReminderAnchor = z.infer<typeof anchorMetaSchema>;

export const reminderMetadataSchema = z.object({
    anchors: z.array(anchorMetaSchema),
    audiences: z.array(z.string()),
    guards: z.array(z.string()),
    sender_accounts: z.array(z.string()),
    channels: z.array(z.string()),
});

export type IReminderMetadata = z.infer<typeof reminderMetadataSchema>;

export const reminderPreviewSchema = z.object({
    rule_id: z.number(),
    rule_name: z.string(),
    new_jobs: z.number(),
    existing_jobs: z.number(),
    due_jobs: z.number(),
    expected_recipients: z.number(),
    invalid_recipients: z.number(),
    expected_sends: z.number(),
});

export type IReminderPreview = z.infer<typeof reminderPreviewSchema>;

export const reminderTestSendResultSchema = z.object({
    detail: z.string(),
    to: z.string(),
});

export type IReminderTestSendResult = z.infer<typeof reminderTestSendResultSchema>;
